import type { RequesterType } from "@/server/constants/persona";
import { ConsentPage } from "./ConsentPage";
import { ExitSetupDialog } from "./ExitSetupDialog";
import { ConsentBlocked, ConsentLoadError, SetupClosed } from "./ConsentMessage";
import type { ConsentState } from "@/frontend/types/consent";

type ConsentScreenProps = {
  /** The consent state from `useConsent()`. */
  consent: ConsentState;
  /** Organization asking for the connection, named in the consent copy. */
  organizationName?: string | null;
  /**
   * The kind of requester, from the `requesterType` of the active workspace.
   * The copy does not name the kind when this value is null or absent.
   */
  requesterType?: RequesterType | null;
  /**
   * True for the individual workflow. In that workflow the reader is the
   * patient, and the records are their own.
   */
  ownRecords?: boolean;
  /** The patient confirms the exit. Send them back to their application, or call `consent.closeSetup()`. */
  onLeave: () => void;
  /** True while the exit is in progress. */
  isLeaving?: boolean;
  /** True when the last exit attempt failed. */
  leaveFailed?: boolean;
};

/**
 * Shows the consent screen for the current status. All entry points use this
 * component. Thus no entry point assembles the screens again.
 *
 * This component returns null for the `loading` and `accepted` statuses. Only
 * the caller knows its own loading state, and only the caller knows the content
 * behind consent.
 */
export function ConsentScreen({
  consent,
  organizationName,
  requesterType,
  ownRecords = false,
  onLeave,
  isLeaving = false,
  leaveFailed = false,
}: ConsentScreenProps) {
  if (consent.isSetupClosed) {
    return <SetupClosed />;
  }

  if (consent.status === "blocked") {
    return <ConsentBlocked />;
  }

  if (consent.status === "error") {
    return <ConsentLoadError onRetry={consent.retry} isRetrying={consent.isRetrying} />;
  }

  if (consent.status !== "needed") {
    return null;
  }

  return (
    <>
      <ConsentPage
        organizationName={organizationName}
        requesterType={requesterType}
        ownRecords={ownRecords}
        onAgree={consent.accept}
        isSubmitting={consent.isAccepting}
        hasError={consent.acceptFailed}
        onExit={() => consent.setExitOpen(true)}
      />
      <ExitSetupDialog
        open={consent.isExitOpen}
        onOpenChange={consent.setExitOpen}
        onLeave={onLeave}
        isLeaving={isLeaving}
        hasError={leaveFailed}
      />
    </>
  );
}
